<template>
  <el-config-provider :locale="elLocale" :size="'default'">
    <router-view v-slot="{ Component }">
      <Transition name="fade" mode="out-in">
        <LoadingPage v-if="showLoading" key="loading" />
        <component :is="Component" v-else key="app" />
      </Transition>
    </router-view>
  </el-config-provider>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { ElConfigProvider } from "element-plus";
// Phase 12.3 (audit M-3) — element-plus DOES ship a Thai locale (verified in
// node_modules/element-plus/dist/locale/th.js). The CLAUDE.md comment that
// "no Thai locale ships" is outdated. Wire it up so DatePicker/Pagination/
// Calendar render Thai strings without our own override table.
import thLocale from "element-plus/es/locale/lang/th";
import enLocale from "element-plus/es/locale/lang/en";
import { useAuthStore } from "@/stores/auth";
import LoadingPage from "@/views/LoadingPage.vue";

const { locale } = useI18n();
const elLocale = computed(() => (locale.value === "th" ? thLocale : enLocale));

const auth = useAuthStore();

// Phase 12.4 — splash duration logic.
//
// Previous attempts (MIN_SPLASH_MS=2500 + sticky guard) still produced a
// 2-round flicker because:
//   1. App.vue splash showed for 2.5s (branded artwork + dot spinner)
//   2. Then CustomerRegisterPage's v-loading-pane took over (different
//      gray Element-Plus spinner) until LIFF init + /auth/line/state
//      resolved.
// Users perceived this as "loading screen #1 → PIN → loading screen #2 →
// PIN" because the two visuals were distinct.
//
// Current approach:
//   • Drop the 2.5s minimum entirely. Splash dismisses as soon as
//     auth.bootstrap() finishes — typically <200ms, often instant when
//     no session hint exists.
//   • CustomerRegisterPage (see this file's sibling) now renders the
//     SAME LoadingPage component during state.step === 'liff-loading'
//     so the visual is continuous: one branded splash from initial mount
//     until the PIN/preliminary/etc step is ready to render.
//   • Sticky guard stays so Pinia reactivity can't flip the splash back
//     on after dismissal.
const hasShown = ref(false);
watchEffect(() => {
  if (!hasShown.value && auth.ready) {
    hasShown.value = true;
  }
});
// Safety fallback: ensure splash is never permanently stuck if auth fails or is skipped
setTimeout(() => {
  if (!hasShown.value) {
    hasShown.value = true;
  }
}, 1200);
const showLoading = computed(() => !hasShown.value);
</script>

<style>
html,
body,
#app {
  height: 100%;
  margin: 0;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}
.app-bg {
  min-height: 100vh;
  background: var(--tm-bg);
  background-size: 300% 300%;
  animation: gradientFlow 15s ease infinite;
}
@keyframes gradientFlow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
